import { type FunctionComponent } from 'preact'
import { html } from 'htm/preact'
import { signal, computed, batch } from '@preact/signals'
import '@substrate-system/input'
import '@substrate-system/button'
import type { AppState } from '../../state.js'
import {
    ToolPage,
    Disclaimer,
    ToolWorkbench,
} from '../../components/tool-page.js'
import {
    calcLeakCost,
    type LeakCostInput,
    type LeakCostBreakdown,
} from '../../util/leak-cost.js'
import { type ApplianceType } from '../../util/leak-rate.js'
import { REFRIGERANTS } from '../../data/refrigerants.js'
import { exportPdf } from '../../util/pdf.js'

const EQUIPMENT_TYPES = [
    'Rooftop Unit (RTU)',
    'Split System',
    'Chiller',
    'Walk-in Cooler',
    'Walk-in Freezer',
    'Refrigerated Display Case',
    'Industrial Process Chiller',
    'Other',
]

const APPLIANCE_LABELS:Record<ApplianceType, string> = {
    commercial: 'Commercial Refrigeration',
    industrial: 'Industrial Process Refrigeration',
    comfort: 'Comfort Cooling',
    other: 'Other',
}

// Form signals
const refrigerantType = signal<string>('R-410A')
const pricePerLb = signal<string>('10')
const lbsLostPerEvent = signal<string>('5')
const eventsPerYear = signal<string>('2')
const equipmentType = signal<string>('Rooftop Unit (RTU)')
const applianceType = signal<ApplianceType>('commercial')
const fullCharge = signal<string>('50')
const efficiencyLossPercent = signal<string>('5')
const pricePerKwh = signal<string>('0.12')
const annualKwh = signal<string>('50000')
const routineCallCost = signal<string>('200')
const emergencyCallsPerYear = signal<string>('1')
const emergencyCallCost = signal<string>('400')
const submitted = signal(false)
/* Bumped on reset to remount the substrate-input web components, which
   do not sync a programmatic value change back to their inner input. */
const formNonce = signal(0)

const result = computed(() => {
    if (!submitted.value) return null
    const input:LeakCostInput = {
        applianceType: applianceType.value,
        pricePerLb: parseFloat(pricePerLb.value),
        lbsLostPerEvent: parseFloat(lbsLostPerEvent.value),
        eventsPerYear: parseFloat(eventsPerYear.value),
        fullCharge: parseFloat(fullCharge.value),
        efficiencyLossPercent: parseFloat(efficiencyLossPercent.value),
        pricePerKwh: parseFloat(pricePerKwh.value),
        annualKwh: parseFloat(annualKwh.value),
        routineCallCost: parseFloat(routineCallCost.value),
        emergencyCallsPerYear: parseFloat(emergencyCallsPerYear.value),
        emergencyCallCost: parseFloat(emergencyCallCost.value),
    }
    const values = Object.values(input) as (number | string)[]
    if (values.some(v => typeof v === 'number' && isNaN(v))) return null
    return calcLeakCost(input)
})

function fmt (n:number):string {
    return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

function fmtPct (n:number):string {
    return `${n.toFixed(1)}%`
}

function handleSubmit (e:Event) {
    e.preventDefault()
    submitted.value = true
}

function handleReset (e:Event) {
    /* substrate-button does not forward type="button", so the inner button
       defaults to submit. Cancel the implicit form submission here. */
    e.preventDefault()
    batch(() => {
        refrigerantType.value = 'R-410A'
        pricePerLb.value = '10'
        lbsLostPerEvent.value = '5'
        eventsPerYear.value = '2'
        equipmentType.value = 'Rooftop Unit (RTU)'
        applianceType.value = 'commercial'
        fullCharge.value = '50'
        efficiencyLossPercent.value = '5'
        pricePerKwh.value = '0.12'
        annualKwh.value = '50000'
        routineCallCost.value = '200'
        emergencyCallsPerYear.value = '1'
        emergencyCallCost.value = '400'
        submitted.value = false
        formNonce.value += 1
    })
}

function handleDownloadPdf (e:Event) {
    e.preventDefault()
    const r = result.value
    if (!r) return
    exportPdf({
        title: 'Refrigerant Leak Cost Report',
        sections: [
            {
                heading: 'Inputs',
                rows: [
                    { label: 'Refrigerant Type', value: refrigerantType.value },
                    { label: 'Price per lb', value: `$${pricePerLb.value}` },
                    {
                        label: 'Lbs Lost per Event',
                        value: lbsLostPerEvent.value,
                    },
                    {
                        label: 'Events per Year',
                        value: eventsPerYear.value,
                    },
                    { label: 'Equipment Type', value: equipmentType.value },
                    {
                        label: 'Appliance Type',
                        value: APPLIANCE_LABELS[applianceType.value],
                    },
                    {
                        label: 'Full System Charge (lbs)',
                        value: fullCharge.value,
                    },
                    {
                        label: 'Efficiency Loss (%)',
                        value: efficiencyLossPercent.value,
                    },
                    {
                        label: 'Energy Cost ($/kWh)',
                        value: pricePerKwh.value,
                    },
                    { label: 'Annual kWh', value: annualKwh.value },
                    {
                        label: 'Routine Call Cost',
                        value: `$${routineCallCost.value}`,
                    },
                    {
                        label: 'Emergency Calls/Year',
                        value: emergencyCallsPerYear.value,
                    },
                    {
                        label: 'Emergency Call Cost',
                        value: `$${emergencyCallCost.value}`,
                    },
                ],
            },
            {
                heading: 'Annual Cost Summary',
                rows: [
                    {
                        label: 'Refrigerant Replacement',
                        value: fmt(r.breakdown.replacement),
                    },
                    { label: 'Energy Waste', value: fmt(r.breakdown.energy) },
                    {
                        label: 'Routine Service',
                        value: fmt(r.breakdown.routine),
                    },
                    {
                        label: 'Emergency Service',
                        value: fmt(r.breakdown.emergency),
                    },
                    { label: 'Annual Total', value: fmt(r.annualTotal) },
                    { label: 'Monthly Total', value: fmt(r.monthlyTotal) },
                    {
                        label: '5-Year Projection',
                        value: fmt(r.fiveYearTotal),
                    },
                ],
            },
            {
                heading: 'EPA Leak Rate Assessment',
                rows: [
                    {
                        label: 'Annualized Leak Rate',
                        value: fmtPct(r.leakRatePercent),
                    },
                    {
                        label: 'Threshold',
                        value: r.leakRateThreshold !== null ?
                            fmtPct(r.leakRateThreshold) :
                            'N/A',
                    },
                    {
                        label: 'Status',
                        value: r.exceedsThreshold === true ?
                            'EXCEEDS threshold' :
                            (r.exceedsThreshold === false ?
                                'Compliant' :
                                'No threshold (Other)'
                            ),
                    },
                ],
            },
        ],
        footer: 'Educational estimate only. Not legal advice. Does not ' +
            'predict enforcement outcomes.',
    })
}

const BREAKDOWN_LABELS:{ key:keyof LeakCostBreakdown, label:string }[] = [
    { key: 'replacement', label: 'Refrigerant Replacement' },
    { key: 'energy', label: 'Energy Waste' },
    { key: 'routine', label: 'Routine Service' },
    { key: 'emergency', label: 'Emergency Service' },
]

export const LeakCostCalculator:FunctionComponent<{
    state:AppState
}> = function LeakCostCalculator () {
    const r = result.value
    const nonce = formNonce.value

    const status = r === null ?
        'neutral' :
        r.exceedsThreshold === true ?
            'exceeds' :
            r.exceedsThreshold === false ?
                'compliant' :
                'neutral'

    return html`
        <${ToolPage}
            title="Refrigerant Leak Cost Calculator"
            description="Estimate the annual cost of an unrepaired refrigerant
                leak broken down by replacement, energy waste, and service
                expenses."
        >
            <${ToolWorkbench}
                emptyHint="Enter refrigerant, energy, and service costs,
                    then select Calculate."
            >
                <form class="tool-form" onSubmit=${handleSubmit} novalidate>

                <div class="tool-form-section">
                    <p class="tool-section-label">Refrigerant</p>
                    <div class="tool-grid">
                        <div class="tool-field">
                            <label class="tool-field-label"
                                for="lcc-refrigerant-type">
                                Refrigerant Type
                            </label>
                            <select
                                id="lcc-refrigerant-type"
                                class="tool-select"
                                value=${refrigerantType.value}
                                onChange=${(e:Event) => {
                                    const sel = e.target as HTMLSelectElement
                                    const ref = REFRIGERANTS.find(
                                        r => r.name === sel.value
                                    )
                                    batch(() => {
                                        refrigerantType.value = sel.value
                                        if (ref?.defaultPricePerLb) {
                                            pricePerLb.value =
                                                String(ref.defaultPricePerLb)
                                        }
                                    })
                                }}
                            >
                                ${REFRIGERANTS.map(r =>
                                    html`<option key=${r.id} value=${r.name}>
                                        ${r.name}
                                    </option>`
                                )}
                            </select>
                        </div>

                        <div class="tool-field">
                            <substrate-input
                                key=${`pricePerLb-${nonce}`}
                                label="Price per lb ($)"
                                type="number"
                                inputmode="decimal"
                                min="0"
                                step="any"
                                placeholder="10"
                                value=${pricePerLb.value}
                                onInput=${(e:Event) => {
                                    pricePerLb.value =
                                        (e.target as HTMLInputElement).value
                                }}
                            ></substrate-input>
                        </div>

                        <div class="tool-field">
                            <substrate-input
                                key=${`lbsLostPerEvent-${nonce}`}
                                label="Lbs Lost per Leak Event"
                                type="number"
                                inputmode="decimal"
                                min="0"
                                step="any"
                                placeholder="5"
                                value=${lbsLostPerEvent.value}
                                onInput=${(e:Event) => {
                                    lbsLostPerEvent.value =
                                        (e.target as HTMLInputElement).value
                                }}
                            ></substrate-input>
                        </div>

                        <div class="tool-field">
                            <substrate-input
                                key=${`eventsPerYear-${nonce}`}
                                label="Leak Events per Year"
                                type="number"
                                inputmode="decimal"
                                min="0"
                                step="any"
                                placeholder="2"
                                value=${eventsPerYear.value}
                                onInput=${(e:Event) => {
                                    eventsPerYear.value =
                                        (e.target as HTMLInputElement).value
                                }}
                            ></substrate-input>
                        </div>
                    </div>
                </div>

                <div class="tool-form-section">
                    <p class="tool-section-label">System</p>
                    <div class="tool-grid">
                        <div class="tool-field">
                            <label class="tool-field-label"
                                for="lcc-equipment-type">
                                Equipment Type
                            </label>
                            <select
                                id="lcc-equipment-type"
                                class="tool-select"
                                value=${equipmentType.value}
                                onChange=${(e:Event) => {
                                    equipmentType.value =
                                        (e.target as HTMLSelectElement).value
                                }}
                            >
                                ${EQUIPMENT_TYPES.map(t =>
                                    html`<option key=${t} value=${t}>
                                        ${t}
                                    </option>`
                                )}
                            </select>
                        </div>

                        <div class="tool-field">
                            <label class="tool-field-label"
                                for="lcc-appliance-type">
                                Appliance Type (for EPA threshold)
                            </label>
                            <select
                                id="lcc-appliance-type"
                                class="tool-select"
                                value=${applianceType.value}
                                onChange=${(e:Event) => {
                                    applianceType.value =
                                        (e.target as HTMLSelectElement)
                                            .value as ApplianceType
                                }}
                            >
                                ${Object.entries(APPLIANCE_LABELS).map(
                                    ([val, label]) =>
                                        html`<option key=${val} value=${val}>
                                            ${label}
                                        </option>`
                                )}
                            </select>
                        </div>

                        <div class="tool-field">
                            <substrate-input
                                key=${`fullCharge-${nonce}`}
                                label="Full System Charge (lbs)"
                                type="number"
                                inputmode="decimal"
                                min="0.01"
                                step="any"
                                placeholder="50"
                                value=${fullCharge.value}
                                onInput=${(e:Event) => {
                                    fullCharge.value =
                                        (e.target as HTMLInputElement).value
                                }}
                            ></substrate-input>
                        </div>
                    </div>
                </div>

                <div class="tool-form-section">
                    <p class="tool-section-label">Energy</p>
                    <div class="tool-grid">
                        <div class="tool-field">
                            <substrate-input
                                key=${`efficiencyLossPercent-${nonce}`}
                                label="Efficiency Loss from Leak (%)"
                                type="number"
                                inputmode="decimal"
                                min="0"
                                step="any"
                                placeholder="5"
                                value=${efficiencyLossPercent.value}
                                onInput=${(e:Event) => {
                                    efficiencyLossPercent.value =
                                        (e.target as HTMLInputElement).value
                                }}
                            ></substrate-input>
                        </div>

                        <div class="tool-field">
                            <substrate-input
                                key=${`pricePerKwh-${nonce}`}
                                label="Electricity Rate ($/kWh)"
                                type="number"
                                inputmode="decimal"
                                min="0"
                                step="any"
                                placeholder="0.12"
                                value=${pricePerKwh.value}
                                onInput=${(e:Event) => {
                                    pricePerKwh.value =
                                        (e.target as HTMLInputElement).value
                                }}
                            ></substrate-input>
                        </div>

                        <div class="tool-field">
                            <substrate-input
                                key=${`annualKwh-${nonce}`}
                                label="Annual Energy Usage (kWh)"
                                type="number"
                                inputmode="decimal"
                                min="0"
                                step="any"
                                placeholder="50000"
                                value=${annualKwh.value}
                                onInput=${(e:Event) => {
                                    annualKwh.value =
                                        (e.target as HTMLInputElement).value
                                }}
                            ></substrate-input>
                        </div>
                    </div>
                </div>

                <div class="tool-form-section">
                    <p class="tool-section-label">Service</p>
                    <div class="tool-grid">
                        <div class="tool-field">
                            <substrate-input
                                key=${`routineCallCost-${nonce}`}
                                label="Routine Service Call Cost ($)"
                                type="number"
                                inputmode="decimal"
                                min="0"
                                step="any"
                                placeholder="200"
                                value=${routineCallCost.value}
                                onInput=${(e:Event) => {
                                    routineCallCost.value =
                                        (e.target as HTMLInputElement).value
                                }}
                            ></substrate-input>
                        </div>

                        <div class="tool-field">
                            <substrate-input
                                key=${`emergencyCallsPerYear-${nonce}`}
                                label="Extra Emergency Calls per Year"
                                type="number"
                                inputmode="decimal"
                                min="0"
                                step="any"
                                placeholder="1"
                                value=${emergencyCallsPerYear.value}
                                onInput=${(e:Event) => {
                                    emergencyCallsPerYear.value =
                                        (e.target as HTMLInputElement).value
                                }}
                            ></substrate-input>
                        </div>

                        <div class="tool-field">
                            <substrate-input
                                key=${`emergencyCallCost-${nonce}`}
                                label="Emergency Call Cost ($)"
                                type="number"
                                inputmode="decimal"
                                min="0"
                                step="any"
                                placeholder="400"
                                value=${emergencyCallCost.value}
                                onInput=${(e:Event) => {
                                    emergencyCallCost.value =
                                        (e.target as HTMLInputElement).value
                                }}
                            ></substrate-input>
                        </div>
                    </div>
                </div>

                <div class="tool-actions">
                    <substrate-button class="primary" type="submit">
                        Calculate
                    </substrate-button>
                    <substrate-button
                        type="button"
                        onClick=${handleReset}
                    >
                        Reset
                    </substrate-button>
                </div>
            </form>

            ${r && html`
                <div class="tool-readout" data-status=${status}>
                    <div class="tool-metrics">
                        <div class="tool-metric">
                            <p class="tool-metric-label">Annual Total</p>
                            <p class="tool-metric-value">
                                ${fmt(r.annualTotal)}
                            </p>
                        </div>
                        <div class="tool-metric">
                            <p class="tool-metric-label">Monthly</p>
                            <p class="tool-metric-value">
                                ${fmt(r.monthlyTotal)}
                            </p>
                        </div>
                        <div class="tool-metric">
                            <p class="tool-metric-label">5-Year Projection</p>
                            <p class="tool-metric-value">
                                ${fmt(r.fiveYearTotal)}
                            </p>
                        </div>
                    </div>

                    <h3>Cost Breakdown</h3>
                    <div class="tool-table-wrap">
                        <table class="tool-table">
                            <thead>
                                <tr>
                                    <th>Category</th>
                                    <th>Annual Cost</th>
                                    <th>Share</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${BREAKDOWN_LABELS.map(({ key, label }) =>
                                    html`<tr key=${key}>
                                        <td>${label}</td>
                                        <td>${fmt(r.breakdown[key])}</td>
                                        <td>
                                            ${fmtPct(r.breakdownPercent[key])}
                                        </td>
                                    </tr>`
                                )}
                            </tbody>
                        </table>
                    </div>

                    <p class="tool-readout-detail">
                        Annualized Leak Rate: ${fmtPct(r.leakRatePercent)}
                    </p>
                    ${r.leakRateThreshold !== null ?
                        html`<p class="tool-status" data-status=${
                            r.exceedsThreshold ? 'exceeds' : 'compliant'
                        }>
                            ${r.exceedsThreshold ?
                                `EXCEEDS threshold (${r.leakRateThreshold}%)` :
                                `Compliant (threshold ${r.leakRateThreshold}%)`
                            }
                        </p>` :
                        html`<p class="tool-status" data-status="neutral">
                            No EPA threshold for this appliance type.
                        </p>`
                    }

                    <div class="tool-actions">
                        <substrate-button
                            type="button"
                            onClick=${handleDownloadPdf}
                        >
                            Download PDF Report
                        </substrate-button>
                        <substrate-button
                            type="button"
                            onClick=${handleReset}
                        >
                            Reset
                        </substrate-button>
                    </div>
                </div>
            `}
            </${ToolWorkbench}>

            <${Disclaimer} />
        </${ToolPage}>
    `
}
